import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from '../auth/auth.service';

// EN-18175 - where a terminated employee lands when they open something outside their restricted view.
export const NOT_AVAILABLE_ROUTE = '/not-available';

// EN-18175 - the only Documents tab a terminated employee keeps.
export const TERMINATED_DOCUMENTS_TAB = 'annual-tax-forms';

/**
 * EN-18175 - for routes that no module right covers (time and attendance, swipe clock, onboarding): a
 * terminated employee is sent to the "not available" page. Everyone else passes untouched.
 */
export const terminatedRestrictionGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return true;
  }

  await authService.refreshEmployeeAccessModules();
  return authService.isTerminatedRestricted ? router.createUrlTree([NOT_AVAILABLE_ROUTE]) : true;
};

/**
 * EN-18175 - a terminated employee only keeps Documents -> Annual Tax Forms. Opening Documents itself
 * moves them to that tab; opening General or Requested Forms directly shows "not available".
 */
export const terminatedDocumentsTabGuard: CanActivateFn = async (route): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isAuthenticated()) {
    return true;
  }

  await authService.refreshEmployeeAccessModules();
  if (!authService.isTerminatedRestricted) {
    return true;
  }

  return route.routeConfig?.path === ''
    ? router.createUrlTree(['/documents', TERMINATED_DOCUMENTS_TAB])
    : router.createUrlTree([NOT_AVAILABLE_ROUTE]);
};
